import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  requestOTP: (phone) => api.post('/auth/otp/request', { phone }),
  verifyOTP: (phone, otp) => api.post('/auth/otp/verify', { phone, otp }),
  otpLogin: (phone, otp) => api.post('/auth/otp/login', { phone, otp }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// User API
export const userAPI = {
  getProfile: () => api.get('/users/me'),
  updateProfile: (data) => api.put('/users/me', data),
  uploadAvatar: (data) => api.post('/users/me/avatar', data),
  deleteAvatar: () => api.delete('/users/me/avatar'),
  getLeaderboard: (limit = 50) => api.get(`/users/leaderboard?limit=${limit}`),
  getUserById: (id) => api.get(`/users/${id}`),
  // Favorite Players (Stammspieler)
  getFavorites: () => api.get('/users/me/favorites'),
  addFavorite: (playerId) => api.post(`/users/me/favorites/${playerId}`),
  removeFavorite: (playerId) => api.delete(`/users/me/favorites/${playerId}`),
  searchPlayers: (query) => api.get(`/players/search?q=${encodeURIComponent(query)}`),
};

// Field API
export const fieldAPI = {
  create: (data) => api.post('/fields', data),
  getAll: (params) => api.get('/fields', { params }),
  getAvailable: (date, location) => api.get('/fields/available', { params: { date, location } }),
  getMy: () => api.get('/fields/my'),
  getById: (id) => api.get(`/fields/${id}`),
  getAvailability: (id, date) => api.get(`/fields/${id}/availability`, { params: { date } }),
  update: (id, data) => api.put(`/fields/${id}`, data),
  delete: (id) => api.delete(`/fields/${id}`),
};

// Match API
export const matchAPI = {
  create: (data) => api.post('/matches', data),
  getAll: (params) => api.get('/matches', { params }),
  getMy: () => api.get('/matches/my'),
  getById: (id) => api.get(`/matches/${id}`),
  apply: (matchId, message) => api.post(`/matches/${matchId}/apply`, { message }),
  acceptApplication: (matchId, userId) => api.post(`/matches/${matchId}/accept/${userId}`),
  rejectApplication: (matchId, userId) => api.post(`/matches/${matchId}/reject/${userId}`),
  complete: (matchId, winnerIds) => api.post(`/matches/${matchId}/complete`, winnerIds),
  submitResult: (matchId, homeScore, awayScore) => api.post(`/matches/${matchId}/submit-result`, { home_score: homeScore, away_score: awayScore }),
  // Invitations
  invitePlayers: (matchId, playerIds) => api.post(`/matches/${matchId}/invite`, playerIds),
  getMyInvitations: () => api.get('/matches/invitations/my'),
  acceptInvitation: (matchId) => api.post(`/matches/${matchId}/invitation/accept`),
  declineInvitation: (matchId) => api.post(`/matches/${matchId}/invitation/decline`),
};

// Team API
export const teamAPI = {
  getAll: (limit = 100) => api.get(`/teams?limit=${limit}`),
  getMy: () => api.get('/teams/my'),
  getAvailable: () => api.get('/teams/available'),
  getById: (id) => api.get(`/teams/${id}`),
  create: (name, description, maxMembers) => api.post(`/teams?name=${encodeURIComponent(name)}&description=${encodeURIComponent(description || '')}&max_members=${maxMembers || 11}`),
  join: (teamId, message) => api.post(`/teams/${teamId}/join?message=${encodeURIComponent(message || '')}`),
  accept: (teamId, userId) => api.post(`/teams/${teamId}/accept/${userId}`),
  reject: (teamId, userId) => api.post(`/teams/${teamId}/reject/${userId}`),
  leave: (teamId) => api.post(`/teams/${teamId}/leave`),
  getApplications: (teamId) => api.get(`/teams/${teamId}/applications`),
  uploadLogo: (teamId, data) => api.post(`/teams/${teamId}/logo`, data),
  deleteLogo: (teamId) => api.delete(`/teams/${teamId}/logo`),
};

// Wallet API
export const walletAPI = {
  getBalance: () => api.get('/wallet/balance'),
  topup: (amount, paymentMethod) => api.post('/wallet/topup', { amount, payment_method: paymentMethod }),
  getTransactions: (limit = 50) => api.get(`/wallet/transactions?limit=${limit}`),
};

// Booking API
export const bookingAPI = {
  create: (data) => api.post('/bookings', data),
  getMy: (status) => api.get('/bookings/my', { params: { status } }),
  getFieldBookings: (fieldId, date) => api.get(`/bookings/field/${fieldId}`, { params: { date } }),
  cancel: (id) => api.delete(`/bookings/${id}`),
};

// Field Owner API
export const ownerAPI = {
  getDashboard: () => api.get('/owner/dashboard'),
  getBookings: (params) => api.get('/owner/bookings', { params }),
  confirmBooking: (id) => api.put(`/owner/bookings/${id}/confirm`),
  rejectBooking: (id) => api.put(`/owner/bookings/${id}/reject`),
  completeBooking: (id) => api.put(`/owner/bookings/${id}/complete`),
  getEarnings: () => api.get('/owner/earnings'),
  getCalendar: (month) => api.get('/owner/calendar', { params: { month } }),
  // Blocking
  createBlock: (data) => api.post('/owner/blocks', data),
  getBlocks: (params) => api.get('/owner/blocks', { params }),
  deleteBlock: (id) => api.delete(`/owner/blocks/${id}`),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getUsers: (limit = 100) => api.get(`/admin/users?limit=${limit}`),
  getFieldOwners: () => api.get('/admin/users?role=field_owner'),
  updateCommission: (userId, rate) => api.put(`/admin/field-owners/${userId}/commission?commission_rate=${rate}`),
  getTransactions: (limit = 100) => api.get(`/admin/transactions?limit=${limit}`),
  // Match Results
  getPendingResults: () => api.get('/admin/pending-results'),
  confirmResult: (matchId, homeScore, awayScore) => api.put(`/admin/matches/${matchId}/confirm-result`, { home_score: homeScore, away_score: awayScore }),
  rejectResult: (matchId, reason) => api.put(`/admin/matches/${matchId}/reject-result?reason=${encodeURIComponent(reason)}`),
  // Result Reminders
  getMatchesAwaitingResults: () => api.get('/admin/matches-awaiting-results'),
  triggerResultReminders: () => api.post('/admin/trigger-result-reminders'),
};

// Notification API
export const notificationAPI = {
  getAll: (limit = 50) => api.get(`/notifications?limit=${limit}`),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.post(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Challenge API
export const challengeAPI = {
  create: (data) => api.post('/challenges', null, { params: data }),
  getAll: (status) => api.get('/challenges', { params: { status } }),
  getById: (id) => api.get(`/challenges/${id}`),
  accept: (id) => api.post(`/challenges/${id}/accept`),
  reject: (id) => api.post(`/challenges/${id}/reject`),
  cancel: (id) => api.post(`/challenges/${id}/cancel`),
  getAvailableTeams: () => api.get('/teams/available'),
};

export default api;
